// src/pages/RandevuDetay.js

import React, { useState, useEffect } from 'react';
import { Button, TextField, Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, Grid, Typography, IconButton } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { Loading } from '../components/common/Loading';

import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Modular Components & Hook
import {
    useAppointmentForm,
    CustomerAutocomplete,
    NewDeviceFields
} from '../modules/appointments';

import CustomerFormModal from '../components/modals/CustomerFormModal';
import { createCustomer } from '../services/customerService';
import GlassCard from '../ui/glass/components/GlassCard';
import usePermissions from '../hooks/usePermissions';

const hizmetler = ['Muayene', 'Kontrol', 'Cihaz Tamiri', 'Ödeme', 'Diğer', 'Uyku Testi', 'Maske Denemesi'];

const RandevuDetay = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canDelete } = usePermissions();

    const [randevuToEdit, setRandevuToEdit] = useState(null);
    const [loadingData, setLoadingData] = useState(!!id);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

    // Fetch appointment data if ID exists
    useEffect(() => {
        const fetchRandevu = async () => {
            if (!id) return;
            try {
                const docRef = doc(db, 'appointments', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setRandevuToEdit({ id: docSnap.id, ...docSnap.data() });
                } else {
                    alert("Randevu bulunamadı!");
                    navigate('/randevular');
                }
            } catch (error) {
                console.error("Randevu çekilirken hata:", error);
            } finally {
                setLoadingData(false);
            }
        };
        fetchRandevu();
    }, [id, navigate]);

    // useAppointmentForm hook'u modal için tasarlandığı için isOpen hep true, onClose navigate.
    // randevuToEdit yüklendikten sonra (veya null ise yeni) hook'a veriyoruz.
    const {
        customers,
        selectedCustomer,
        customerInputValue,
        setCustomerInputValue,
        telefon,
        setTelefon,
        addDevice,
        setAddDevice,
        seriNumarasi,
        setSeriNumarasi,
        marka,
        setMarka,
        model,
        setModel,
        cihazSinifi,
        setCihazSinifi,
        isSgk,
        setIsSgk,
        zimmetTarihi,
        setZimmetTarihi,
        faturaNo,
        setFaturaNo,
        verilisTarihi,
        setVerilisTarihi,
        garantiBaslangic,
        setGarantiBaslangic,
        garantiBitis,
        setGarantiBitis,
        periyodikBakimTarihi,
        setPeriyodikBakimTarihi,
        brandsFromDb,
        randevuTarihi,
        setRandevuTarihi,
        ilgilenilenHizmet,
        setIlgilenilenHizmet,
        notlar,
        setNotlar,
        isSaving,
        handleCustomerChange,
        handleSubmit,
        handleClose, // Bu modal'ı kapatmak içindi, burada navigate kullanacağız
        isEditing
    } = useAppointmentForm(true, () => navigate('/randevular'), randevuToEdit);

    // Loading...
    if (loadingData) {
        return <Loading message="Randevu yükleniyor..." />;
    }

    const handleAddNewCustomer = async (customerData) => {
        try {
            const customerId = await createCustomer(customerData);
            handleCustomerChange(null, { id: customerId, ...customerData });
            setIsCustomerModalOpen(false);
        } catch (error) {
            console.error("Müşteri oluşturma hatası:", error);
            alert("Müşteri oluşturulurken bir hata meydana geldi.");
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        if (window.confirm("Bu randevuyu silmek istediğinizden emin misiniz?")) {
            try {
                await deleteDoc(doc(db, "appointments", id));
                navigate('/randevular');
            } catch (error) {
                console.error("Silme hatası:", error);
                alert("Randevu silinirken hata oluştu.");
            }
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            <GlassCard sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/randevular')} sx={{ color: 'text.secondary' }}>
                            Geri
                        </Button>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {id ? 'Randevu Detayı' : 'Yeni Randevu'}
                        </Typography>
                    </Box>
                    {id && canDelete() && (
                        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
                            Sil
                        </Button>
                    )}
                </Box>

                <Box component="form" noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Grid container spacing={1} alignItems="flex-start">
                        <Grid item xs={10}>
                            <CustomerAutocomplete
                                customers={customers}
                                selectedCustomer={selectedCustomer}
                                handleCustomerChange={handleCustomerChange}
                                customerInputValue={customerInputValue}
                                setCustomerInputValue={setCustomerInputValue}
                                isEditing={isEditing}
                                telefon={telefon}
                                setTelefon={setTelefon}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                sx={{ height: 56 }}
                                onClick={() => setIsCustomerModalOpen(true)}
                                title="Yeni Müşteri Ekle"
                            >
                                <PersonAddIcon />
                            </Button>
                        </Grid>
                    </Grid>

                    {!selectedCustomer && customerInputValue && !isEditing && (
                        <NewDeviceFields
                            addDevice={addDevice}
                            setAddDevice={setAddDevice}
                            seriNumarasi={seriNumarasi}
                            setSeriNumarasi={setSeriNumarasi}
                            marka={marka}
                            setMarka={setMarka}
                            model={model}
                            setModel={setModel}
                            cihazSinifi={cihazSinifi}
                            setCihazSinifi={setCihazSinifi}
                            isSgk={isSgk}
                            setIsSgk={setIsSgk}
                            zimmetTarihi={zimmetTarihi}
                            setZimmetTarihi={setZimmetTarihi}
                            faturaNo={faturaNo}
                            setFaturaNo={setFaturaNo}
                            verilisTarihi={verilisTarihi}
                            setVerilisTarihi={setVerilisTarihi}
                            garantiBaslangic={garantiBaslangic}
                            setGarantiBaslangic={setGarantiBaslangic}
                            garantiBitis={garantiBitis}
                            setGarantiBitis={setGarantiBitis}
                            periyodikBakimTarihi={periyodikBakimTarihi}
                            setPeriyodikBakimTarihi={setPeriyodikBakimTarihi}
                            brandsFromDb={brandsFromDb}
                        />
                    )}

                    <DateTimePicker
                        label="Randevu Tarihi ve Saati *"
                        value={randevuTarihi}
                        onChange={setRandevuTarihi}
                        ampm={false}
                        minutesStep={15}
                    />

                    <FormControl fullWidth>
                        <InputLabel>İlgilenilen Hizmet</InputLabel>
                        <Select value={ilgilenilenHizmet} label="İlgilenilen Hizmet" onChange={(e) => setIlgilenilenHizmet(e.target.value)}>
                            {hizmetler.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Randevu Notları"
                        value={notlar}
                        onChange={(e) => setNotlar(e.target.value)}
                        multiline
                        rows={4}
                        placeholder="Örn: Cihazın periyodik bakımı hakkında görüşülecek."
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button variant="outlined" onClick={() => navigate('/randevular')}>İptal</Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSubmit}
                            disabled={isSaving}
                        >
                            {isSaving ? <CircularProgress size={24} /> : (isEditing ? 'Güncelle' : 'Kaydet')}
                        </Button>
                    </Box>
                </Box>
            </GlassCard>

            <CustomerFormModal
                isOpen={isCustomerModalOpen}
                onClose={() => setIsCustomerModalOpen(false)}
                onAddCustomer={handleAddNewCustomer}
                customerToEdit={null}
            />
        </Box>
    );
};

export default RandevuDetay;
